import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import { Star, ThumbsUp, Meh, ThumbsDown, CheckCircle2, Music, Loader2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

interface FeedbackData {
  id: string;
  customer_name: string;
  customer_email: string;
  event_date: string | null;
  submitted_at: string | null;
  feedback_token_expires_at: string;
}

export default function NPSFeedback() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [expired, setExpired] = useState(false);
  const [feedbackData, setFeedbackData] = useState<FeedbackData | null>(null);
  const [selectedScore, setSelectedScore] = useState<number | null>(null);
  const [feedbackText, setFeedbackText] = useState('');
  const [wouldRecommend, setWouldRecommend] = useState<boolean | null>(null);

  useEffect(() => {
    if (token) {
      loadFeedback();
    }
  }, [token]);

  const loadFeedback = async () => {
    try {
      const { data, error } = await supabase
        .from('customer_feedback')
        .select('id, customer_name, customer_email, event_date, submitted_at, feedback_token_expires_at')
        .eq('feedback_token', token)
        .single();

      if (error || !data) {
        setExpired(true);
        return;
      }

      // Check if already submitted
      if (data.submitted_at) {
        setSubmitted(true);
        setFeedbackData(data);
        return;
      }

      // Check if expired
      if (new Date(data.feedback_token_expires_at) < new Date()) {
        setExpired(true);
        return;
      }

      setFeedbackData(data);
    } catch (error) {
      console.error('Error loading feedback:', error);
      setExpired(true);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (selectedScore === null) {
      toast({
        title: 'Please select a score',
        description: 'Choose a number from 0 to 10',
        variant: 'destructive',
      });
      return;
    }

    setSubmitting(true);

    try {
      const { error } = await supabase
        .from('customer_feedback')
        .update({
          nps_score: selectedScore,
          feedback_text: feedbackText || null,
          would_recommend: wouldRecommend,
          submitted_at: new Date().toISOString(),
        })
        .eq('feedback_token', token);

      if (error) throw error;

      setSubmitted(true);
      toast({
        title: 'Thank you! 🎉',
        description: 'Your feedback has been submitted successfully.',
      });
    } catch (error: any) {
      console.error('Error submitting feedback:', error);
      toast({
        title: 'Submission failed',
        description: error.message || 'Please try again',
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const getScoreLabel = (score: number) => {
    if (score >= 9) return 'Excellent!';
    if (score >= 7) return 'Good';
    if (score >= 5) return 'Okay';
    if (score >= 3) return 'Could be better';
    return 'Needs improvement';
  };

  const getScoreColor = (score: number) => {
    if (score >= 9) return 'bg-green-500 text-white border-green-500';
    if (score >= 7) return 'bg-yellow-500 text-white border-yellow-500';
    return 'bg-red-500 text-white border-red-500';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center space-y-4">
          <Loader2 className="h-12 w-12 text-primary mx-auto animate-spin" />
          <p className="text-muted-foreground">Loading survey...</p>
        </div>
      </div>
    );
  }

  if (expired) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="max-w-md w-full text-center">
          <CardHeader>
            <div className="mx-auto mb-4 p-4 bg-muted rounded-full w-fit">
              <Music className="h-8 w-8 text-muted-foreground" />
            </div>
            <CardTitle>Survey Unavailable</CardTitle>
            <CardDescription>
              This feedback survey has expired or is no longer available. 
              If you have questions, please contact us directly.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={() => navigate('/')} variant="outline">
              Visit Our Website
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
        >
          <Card className="max-w-md w-full text-center">
            <CardHeader>
              <motion.div 
                className="mx-auto mb-4"
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
              >
                <div className="p-4 bg-green-500/20 rounded-full w-fit mx-auto">
                  <CheckCircle2 className="h-12 w-12 text-green-500" />
                </div>
              </motion.div>
              <CardTitle className="text-2xl">Thank You! 🎵</CardTitle>
              <CardDescription className="text-base">
                Your feedback helps us create even more memorable experiences. 
                We truly appreciate you taking the time to share your thoughts.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground">
                — The Lost Strings Team
              </p>
              <Button onClick={() => navigate('/')} className="w-full">
                Visit Our Website
              </Button>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background py-8 px-4">
      <div className="max-w-2xl mx-auto space-y-6">
        {/* Header */}
        <motion.div 
          className="text-center space-y-2"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="flex items-center justify-center gap-2 text-primary">
            <Music className="h-6 w-6" />
            <span className="font-semibold text-lg">The Lost Strings</span>
          </div>
          <h1 className="text-3xl font-bold">
            Hi {feedbackData?.customer_name?.split(' ')[0]}! 👋
          </h1>
          <p className="text-muted-foreground">
            Thank you for choosing us. We'd love to hear about your experience!
          </p>
        </motion.div>

        {/* NPS Score Selection */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <Card>
            <CardHeader>
              <CardTitle className="text-center text-xl">
                How likely are you to recommend The Lost Strings?
              </CardTitle>
              <CardDescription className="text-center">
                0 = Not at all likely &nbsp;•&nbsp; 10 = Extremely likely
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Score buttons */}
              <div className="flex flex-wrap justify-center gap-2">
                {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((score) => (
                  <motion.button
                    key={score}
                    onClick={() => setSelectedScore(score)}
                    className={`w-11 h-11 rounded-full border-2 font-semibold transition-all ${
                      selectedScore === score
                        ? getScoreColor(score)
                        : 'border-border hover:border-primary/50 hover:bg-muted'
                    }`}
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    {score}
                  </motion.button>
                ))}
              </div>

              {/* Score feedback */}
              <AnimatePresence mode="wait">
                {selectedScore !== null && (
                  <motion.div
                    key={selectedScore}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="text-center"
                  >
                    <div className="flex items-center justify-center gap-2">
                      {selectedScore >= 9 ? (
                        <ThumbsUp className="h-5 w-5 text-green-500" />
                      ) : selectedScore >= 7 ? (
                        <Meh className="h-5 w-5 text-yellow-500" />
                      ) : (
                        <ThumbsDown className="h-5 w-5 text-red-500" />
                      )}
                      <span className={`font-medium ${
                        selectedScore >= 9 ? 'text-green-500' : 
                        selectedScore >= 7 ? 'text-yellow-500' : 'text-red-500'
                      }`}>
                        {getScoreLabel(selectedScore)}
                      </span>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </CardContent>
          </Card>
        </motion.div>

        {/* Additional Feedback */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">
                What made your experience memorable? (Optional)
              </CardTitle>
              <CardDescription>
                Share what you loved or how we could improve
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Textarea
                value={feedbackText}
                onChange={(e) => setFeedbackText(e.target.value)}
                placeholder="Tell us about your experience..."
                className="min-h-[120px] resize-none"
              />

              {/* Would recommend toggle */}
              <div className="flex items-center gap-4">
                <span className="text-sm text-muted-foreground">Would you book us again?</span>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    size="sm"
                    variant={wouldRecommend === true ? 'default' : 'outline'}
                    onClick={() => setWouldRecommend(true)}
                    className="gap-1"
                  >
                    <ThumbsUp className="h-4 w-4" />
                    Yes
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant={wouldRecommend === false ? 'destructive' : 'outline'}
                    onClick={() => setWouldRecommend(false)}
                    className="gap-1"
                  >
                    <ThumbsDown className="h-4 w-4" />
                    No
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Submit Button */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <Button 
            onClick={handleSubmit} 
            className="w-full h-12 text-lg"
            disabled={submitting || selectedScore === null}
          >
            {submitting ? (
              <>
                <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                Submitting...
              </>
            ) : (
              <>
                <Star className="h-5 w-5 mr-2" />
                Submit Feedback
              </>
            )}
          </Button>
          <p className="text-center text-xs text-muted-foreground mt-3">
            Your feedback is anonymous and helps us improve our services.
          </p>
        </motion.div>
      </div>
    </div>
  );
}
